import { ModelTypes, topliApi, Webinar } from '@/shared/api/topli/TopliApi'
import { CreateWebinarFormState } from '../model/types'
import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query'
import { errorHandler } from '@/shared/lib/errorHandler'
import { addTranslation } from '@/entities/ContentTranslation'
import { currentWebinarByIdQueryKey, webinarListsByPageQueryKey } from '@/entities/Webinars'
import { formatDateForSending } from '@/shared/lib/utils'
import { UploadWebinarPhoto } from '@/entities/Photos/api/photo-webinar'

export const createWebinar = async (state: CreateWebinarFormState): Promise<Webinar> => {
  const { translations, release_date, thumbnail, ...restState } = state

  const body = {
    name: translations.tr.name,
    description: translations.tr.description,
    release_date: release_date ? formatDateForSending(release_date) : null,
    ...restState,
  }

  const response = await topliApi.webinars.create({ item_id: null }, body)
  return response
}

export const useCreateWebinarApi = (): UseMutationResult<
  Webinar,
  Error,
  CreateWebinarFormState
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: state => createWebinar(state),
    onSuccess: async (res, state) => {
      try {
        await addTranslation(
          {
            translations: state.translations,
            modelType: ModelTypes.Webinar,
          },
          res.id,
        )

        if (state.thumbnail && typeof state.thumbnail === 'object') {
          await UploadWebinarPhoto({
            file: state.thumbnail,
            id: res.id,
          })
        }

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [webinarListsByPageQueryKey],
            refetchType: 'active',
          }),
          queryClient.invalidateQueries({
            queryKey: [currentWebinarByIdQueryKey],
            refetchType: 'active',
          }),
        ])
      } catch (error) {
        errorHandler(error)
      }
    },
  })
}
